/**
 * File: adrService.ts
 * Description: Service class for ADR (Architecture Decision Records) operations
 * Author: GitHub Copilot
 * Created: 2025-08-26
 * Last Modified: 2025-08-26
 */

import { ADR, ADRSuggestion, ADRStatus, ADRSource, ADRDefinition, ADRDefinitionStatus, ADRDefinitionUpsert } from '../types/adr';
import { AsyncJobStartResponse } from '../types/asyncJob';
import { logger } from '../utils/logger';

import { apiClient } from './apiClient';
import { startAndPollJob } from './jobPollingService';

interface ExecutionTrace {
  steps?: Array<Record<string, unknown>>;
}

type ExecutionPlanResult = Array<Record<string, unknown>> | string[] | null;

/** Result payload produced by the ADR generation background job. */
interface ADRGenerationJobResult {
  project_id: number;
  adr_id: string;
  success: boolean;
  content?: string;
  error_message?: string;
}

export interface ADRSuggestResponse {
  suggestions: ADRSuggestion[];
  execution_trace?: ExecutionTrace;
}

export interface ADRSuggestRequest {
  projectContext: string;
  maxSuggestions?: number;
  existingADRs?: Array<{
    title: string;
  }>;
}

export class ADRService {
  /**
   * Suggest ADRs based on project context.
   *
   * Runs as a background job to avoid platform request timeouts: it starts
   * the job, then polls until the suggestions are ready.
   *
   * @param projectId - Project ID
   * @param request - Suggestion request parameters
   * @returns Promise with ADR suggestions and execution trace
   */
  public async suggestADRs(projectId: number, request: ADRSuggestRequest): Promise<ADRSuggestResponse> {
    return startAndPollJob<ADRSuggestResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/project/${projectId}/adr/suggest/async`,
          request,
        ),
    );
  }

  /**
   * Generate an ADR using AI.
   *
   * Runs as a background job to avoid platform request timeouts: it starts
   * the job, then polls until the generated content is ready.
   *
   * @param projectId - Project ID
   * @param adrId - The ADR definition ID to generate
   * @returns Promise with the generated ADR content
   */
  public async generateADR(projectId: number, adrId: string): Promise<string> {
    try {
      const result = await startAndPollJob<ADRGenerationJobResult>(
        projectId,
        () =>
          apiClient.post<AsyncJobStartResponse>(
            `/api/project/${projectId}/adr/${adrId}/generate/async`,
            undefined,
          ),
      );
      return result.content ?? '';
    } catch (error) {
      logger.error('Failed to generate ADR:', error);
      throw error;
    }
  }

  /**
   * Build a start-factory that kicks off ADR generation as a background job
   * and resolves with the job id. Used with the navigation-resilient agent-job
   * tracker, which owns the polling so the run survives page navigation.
   *
   * @param projectId - Project ID
   * @param adrId - The ADR definition ID to generate
   * @returns A function that issues the start request (HTTP 202).
   */
  public startGenerateADR(
    projectId: number,
    adrId: string,
  ): () => Promise<AsyncJobStartResponse> {
    return () =>
      apiClient.post<AsyncJobStartResponse>(
        `/api/project/${projectId}/adr/${adrId}/generate/async`,
      );
  }

  // === ADR DEFINITION METHODS ===

  /**
   * Get all ADR definitions for a project
   * @param projectId - Project ID
   * @returns Promise with array of ADR definitions
   */
  public async getADRDefinitions(projectId: number): Promise<ADRDefinition[]> {
    try {
      const response = await apiClient.get<any[]>(`/api/project/${projectId}/adr`);
      
      // Transform snake_case backend fields to camelCase frontend fields
      return response.map(adr => ({
        ...adr,
        createdDate: adr.created_at,
        updatedDate: adr.updated_at,
      }));
      
    } catch (error) {
      logger.error('Failed to fetch ADR definitions:', error);
      return [];
    }
  }

  /**
   * Save (create or update) an ADR definition
   * @param projectId - Project ID
   * @param adrId - ADR ID (optional, for updates)
   * @param definition - ADR definition data
   * @returns Promise with saved ADR definition
   */
  public async saveADRDefinition(projectId: number, definition: ADRDefinitionUpsert, adrId: string | null): Promise<ADRDefinition> {
    try {
      let response: any;
      if (adrId) {
        // Update existing ADR definition
        response = await apiClient.put(`/api/project/${projectId}/adr/${adrId}`, definition);
      } else {
        // Create new ADR definition
        response = await apiClient.post(`/api/project/${projectId}/adr`, definition);
      }
      
      // Transform snake_case backend fields to camelCase frontend fields
      return {
        ...response,
        createdDate: response.created_at,
        updatedDate: response.updated_at,
      };
    } catch (error) {
      logger.error('Failed to save ADR definition:', error);
      throw error;
    }
  }

  /**
   * Delete an ADR definition
   * @param _projectId - Project ID (unused in mock implementation)
   * @param definitionId - ADR definition ID
   */
  public async deleteADRDefinition(projectId: number, adrId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/project/${projectId}/adr/${adrId}`);
    } catch (error) {
      logger.error('Failed to delete ADR definition:', error);
      throw error;
    }
  }

  public async getADRGeneratedContent(projectId: number, adrId: string): Promise<string> {
    try {
      return await apiClient.get(`/api/project/${projectId}/adr/${adrId}/content`);
    } catch (error) {
      logger.error('Failed to get ADR generated content:', error);
      throw error;
    }
  }

  public async saveADRGeneratedContent(projectId: number, adrId: string, content: string): Promise<void> {
    try {
      await apiClient.put(`/api/project/${projectId}/adr/${adrId}/content`, { content });
    } catch (error) {
      logger.error('Failed to save ADR generated content:', error);
      throw error;
    }
  }

  /**
   * Get execution plan for ADR generation process
   * @param projectId - Project ID  
   * @param adrId - ADR ID
   * @returns Promise with detailed execution plan
   */
  public async getADRGenerationExecutionPlan(
    projectId: number,
    adrId: string,
  ): Promise<ExecutionPlanResult> {
    try {
      return await apiClient.get<ExecutionPlanResult>(
        `/api/project/${projectId}/adr/${adrId}/execution_plan`,
      );
    } catch (error) {
      logger.error('Failed to get ADR generation execution plan:', error);
      return null;
    }
  }

}

// Export singleton instance
export const adrService = new ADRService();
